import { type ReactNode } from 'react';

import { cn } from '../lib/utils';

export type RadioColor = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

const colorClasses: Record<RadioColor, { card: string; circle: string; ring: string }> = {
  primary: {
    card: 'border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-950',
    circle: 'border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500',
    ring: 'has-[:focus-visible]:ring-green-500'
  },
  secondary: {
    card: 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950',
    circle: 'border-blue-600 bg-blue-600 dark:border-blue-500 dark:bg-blue-500',
    ring: 'has-[:focus-visible]:ring-blue-500'
  },
  destructive: {
    card: 'border-uniform-red-500 bg-uniform-red-50 dark:border-uniform-red-400 dark:bg-uniform-red-950',
    circle:
      'border-uniform-red-600 bg-uniform-red-600 dark:border-uniform-red-500 dark:bg-uniform-red-500',
    ring: 'has-[:focus-visible]:ring-uniform-red-500'
  },
  warning: {
    card: 'border-yellow-400 bg-yellow-50 dark:border-yellow-400 dark:bg-yellow-950',
    circle: 'border-yellow-500 bg-yellow-500 dark:border-yellow-400 dark:bg-yellow-400',
    ring: 'has-[:focus-visible]:ring-yellow-500'
  },
  neutral: {
    card: 'border-uniform-gray-500 bg-uniform-gray-50 dark:border-uniform-gray-400 dark:bg-uniform-gray-900',
    circle:
      'border-uniform-gray-600 bg-uniform-gray-600 dark:border-uniform-gray-500 dark:bg-uniform-gray-500',
    ring: 'has-[:focus-visible]:ring-uniform-gray-500'
  }
};

type RadioValue = string | number | boolean;

export interface RadioOption {
  className?: string;
  disabled?: boolean;
  label: string | ReactNode;
  value: RadioValue;
}

export interface RadioGroupProps {
  className?: string;
  color?: RadioColor;
  disabled?: boolean;
  helpText?: ReactNode | string;
  id: string;
  label?: string | ReactNode;
  onChange: (value: RadioValue) => void;
  options: RadioOption[];
  optionsClassName?: string;
  selectedValue: RadioValue;
}

function RadioGroup({
  className,
  color = 'primary',
  disabled = false,
  helpText,
  id,
  label,
  onChange,
  options,
  optionsClassName,
  selectedValue
}: RadioGroupProps) {
  const { card, circle, ring } = colorClasses[color];
  return (
    <fieldset disabled={disabled} className={cn('flex flex-col gap-1', className)}>
      {label && (
        <legend className="mb-1 block text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
          {label}
        </legend>
      )}
      <div className={cn('flex flex-wrap items-start gap-2', optionsClassName)}>
        {options.map(
          ({ className: optionCls, disabled: optionDisabled, label: optionLabel, value }) => {
            const optionId = `${id}-radio-${value}`;
            const isSelected = value === selectedValue;
            const isDisabled = disabled || optionDisabled;

            return (
              <div
                key={optionId}
                className={cn(
                  'relative flex items-center gap-2 rounded-md border px-3 py-2 shadow-xs transition-colors',
                  isSelected
                    ? card
                    : 'border-uniform-gray-300 bg-white dark:border-uniform-gray-600 dark:bg-uniform-gray-800',
                  isDisabled && 'opacity-50',
                  optionCls
                )}>
                {/* Visual radio circle */}
                <div
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-offset-1',
                    ring,
                    isSelected
                      ? circle
                      : 'border-uniform-gray-300 bg-white dark:border-uniform-gray-600 dark:bg-uniform-gray-800'
                  )}>
                  {isSelected && (
                    <div
                      className={`h-2 w-2 rounded-full ${color === 'warning' ? 'bg-yellow-950' : 'bg-white dark:bg-white'}`}
                    />
                  )}
                  {/* Native radio: invisible, covers the circle area */}
                  <input
                    id={optionId}
                    type="radio"
                    name={id}
                    value={String(value)}
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => onChange(value)}
                    className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                  />
                </div>
                <label
                  htmlFor={optionId}
                  className={cn(
                    'min-w-0 flex-1 select-none text-sm font-medium leading-6 text-uniform-gray-700 dark:text-uniform-gray-200',
                    !isDisabled && 'cursor-pointer'
                  )}>
                  {optionLabel}
                </label>
              </div>
            );
          }
        )}
      </div>
      {helpText && (
        <div className="mt-1 text-sm text-uniform-gray-600 dark:text-uniform-gray-400">
          {helpText}
        </div>
      )}
    </fieldset>
  );
}

export { RadioGroup };
